import React, { useCallback, useState } from "react";
import { reject } from "lodash";
import styled from "styled-components";
import { useTheme } from "@glossy/theme";
import { Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { Serie } from "../models";
import Chart, { ChartProps } from "./chart";
import { useOtherSeriesIds, useSeries } from "./data";
import Legend, { LegendProps } from "./legend";
export interface IProps {
  series: Serie[];
  range: Range;
  locale: Locale;
  tooltip?: LegendProps["tooltip"];
  total?: LegendProps["total"];
  config?: ChartProps["config"];
  metricLabel?: string;
  withPreviousPeriod?: boolean;
  onClickPoint?: ChartProps["onClickPoint"];
  onClickLegend?: LegendProps["onClickLegend"];
  className?: string;
  chartTooltip?: ChartProps["chartTooltip"];
  scale?: ChartProps["scale"];
  timezone: Timezone;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
  displayVolumes?: boolean;
  legendSort?: "alpha" | "volumeDesc";
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const LineChart = ({
  series,
  range,
  locale,
  tooltip,
  total,
  config,
  chartTooltip,
  onClickPoint,
  onClickLegend,
  className,
  scale = "linear",
  timezone,
  disableAnimation = false,
  isFullscreen,
  displayVolumes,
  legendSort,
}: IProps) => {
  const [hovered, setHovered] = useState<string>(null);
  const theme = useTheme();
  const _series = useSeries(series, locale, theme);
  // The 'other' serie must not be displayed in the chart
  const chartSeries = reject(_series, { id: "other" });
  const otherSeriesId = useOtherSeriesIds(series);

  const _onClickLegend = useCallback(
    (serieId) => {
      if (onClickLegend) {
        if (serieId !== "other") {
          onClickLegend(serieId);
        } else {
          onClickLegend(otherSeriesId);
        }
      }
    },
    [onClickLegend, otherSeriesId]
  );

  const _onClickPoint = useCallback(
    (serieId, range) => {
      if (onClickPoint) {
        onClickPoint(serieId, range);
      }
    },
    [onClickPoint]
  );

  return (
    <Root className={className}>
      <Legend
        series={_series}
        setHovered={setHovered}
        locale={locale}
        onClickLegend={onClickLegend && _onClickLegend}
        tooltip={tooltip}
        total={total}
        range={range}
        disableAnimation={disableAnimation}
        displayVolumes={displayVolumes}
        legendSort={legendSort}
      />
      <Chart
        hovered={hovered}
        chartTooltip={chartTooltip}
        onClickPoint={onClickPoint && _onClickPoint}
        locale={locale}
        range={range}
        series={chartSeries}
        config={config}
        scale={scale || "linear"}
        timezone={timezone}
        disableAnimation={disableAnimation}
        isFullscreen={isFullscreen}
      />
    </Root>
  );
};

export default LineChart;
